import React from "react";
import { Gauge, ListChecks, ShieldAlert } from "lucide-react";

const money = (n) =>
    Number(n || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });

const impactCls = {
    high: "border-red-500/40 bg-red-500/10 text-red-300",
    medium: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    low: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
};

function RangeStrip({ range }) {
    if (!range || !range.realistic) return null;
    const lo = range.optimistic ?? range.realistic;
    const hi = range.conservative ?? range.realistic;
    const span = Math.max(hi - lo, 1);
    const pct = Math.round(((range.realistic - lo) / span) * 100);
    return (
        <div className="rounded-sm border border-white/15 bg-[#0a0a0a] p-3" data-testid="estimate-range">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#8fa4f0] mb-2">Three-point range</div>
            <div className="relative h-2 rounded-full bg-gradient-to-r from-emerald-500/60 via-[#4f6fe8] to-red-500/60">
                <span className="absolute -top-1 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-[#0a0a0a] bg-white" style={{ left: `${pct}%` }} />
            </div>
            <div className="mt-2 grid grid-cols-3 text-xs">
                <div><div className="text-emerald-300 font-mono font-bold" data-testid="estimate-range-optimistic">{money(lo)}</div><div className="text-slate-400">optimistic</div></div>
                <div className="text-center"><div className="text-white font-mono font-bold" data-testid="estimate-range-realistic">{money(range.realistic)}</div><div className="text-slate-400">realistic (quoted)</div></div>
                <div className="text-right"><div className="text-red-300 font-mono font-bold" data-testid="estimate-range-conservative">{money(hi)}</div><div className="text-slate-400">conservative</div></div>
            </div>
            {range.basis && <p className="mt-2 text-[11px] text-slate-400">{range.basis}</p>}
        </div>
    );
}

function Confidence({ confidence }) {
    if (!confidence || confidence.overall == null) return null;
    const v = confidence.overall;
    const tone = v >= 75 ? "text-emerald-300" : v >= 50 ? "text-amber-300" : "text-red-300";
    const cats = Object.entries(confidence.by_category || {});
    return (
        <div className="rounded-sm border border-white/15 bg-[#0a0a0a] p-3" data-testid="estimate-confidence">
            <div className="flex items-center gap-2">
                <Gauge className={`h-4 w-4 ${tone}`} />
                <span className="text-[10px] uppercase tracking-wider font-bold text-[#8fa4f0]">Confidence</span>
                <span className={`ml-auto font-mono text-lg font-bold ${tone}`} data-testid="estimate-confidence-overall">{v}%</span>
            </div>
            {confidence.basis && <p className="mt-1 text-[11px] text-slate-400">{confidence.basis}</p>}
            {cats.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {cats.map(([k, n]) => (
                        <span key={k} className="rounded-full border border-white/15 px-2 py-0.5 text-[11px] text-slate-200">{k} · <span className="font-mono">{n}%</span></span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function EstimateInsights({ estimate }) {
    const assumptions = estimate?.assumptions || [];
    const risks = estimate?.risks || [];
    const has = estimate?.cost_range || estimate?.confidence?.overall != null || assumptions.length || risks.length;
    if (!has) return null;
    return (
        <div className="mt-5 space-y-3" data-testid="estimate-insights">
            <div className="grid gap-3 sm:grid-cols-2">
                <RangeStrip range={estimate.cost_range} />
                <Confidence confidence={estimate.confidence} />
            </div>
            {assumptions.length > 0 && (
                <div className="rounded-sm border border-white/15 bg-[#0a0a0a] p-3" data-testid="estimate-assumptions">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-[#8fa4f0] mb-1"><ListChecks className="h-3.5 w-3.5" /> Assumptions</div>
                    <ul className="text-sm text-slate-200 list-disc list-inside space-y-1">
                        {assumptions.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                </div>
            )}
            {risks.length > 0 && (
                <div className="rounded-sm border border-white/15 bg-[#0a0a0a] p-3" data-testid="estimate-risks">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-[#8fa4f0] mb-2"><ShieldAlert className="h-3.5 w-3.5" /> Risks</div>
                    <ul className="space-y-2">
                        {risks.map((r, i) => (
                            <li key={i} className="text-sm text-slate-200">
                                <span className={`mr-2 inline-block rounded-full border px-1.5 py-px text-[10px] uppercase ${impactCls[r.impact] || impactCls.medium}`}>{r.impact}</span>
                                {r.risk}
                                {r.mitigation && <div className="ml-1 text-[12px] text-slate-400">↳ {r.mitigation}</div>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
